<template>
  <div class="resume-create-container">
    <el-card shadow="never" class="box-card">
      <!-- 头部卡片标题与操作区 -->
      <div slot="header" class="card-header">
        <div class="title-wrapper">
          <i class="el-icon-document-add header-icon" />
          <span class="title-text">编辑个人简历</span>
          <span class="sub-title">修改您的专业经历并随时保存或预览打印 A4 简历</span>
        </div>
        <div class="header-action">
          <el-button
            type="primary"
            icon="el-icon-view"
            size="small"
            @click="handlePreview"
          >
            预览 / 打印 A4 简历
          </el-button>
          <el-button
            type="success"
            icon="el-icon-check"
            size="small"
            :loading="submitLoading"
            @click="handleSubmit"
          >
            保存简历
          </el-button>
        </div>
      </div>

      <!-- 单栏满宽编辑区 -->
      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-width="90px"
        size="small"
        class="resume-form"
      >
        <!-- 1. 基本信息 -->
        <div class="section-title"><i class="el-icon-s-custom" /> 基本信息</div>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="姓名" prop="name">
              <el-input v-model="form.name" placeholder="如：张三" clearable />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="求职意向" prop="targetPosition">
              <el-input v-model="form.targetPosition" placeholder="如：DevOps / Java 研发工程师" clearable />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="手机号" prop="phone">
              <el-input v-model="form.phone" placeholder="如：13800138000" clearable />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="电子邮箱" prop="email">
              <el-input v-model="form.email" placeholder="如：zhangsan@example.com" clearable />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="年龄">
              <el-input v-model="form.age" placeholder="如：28" clearable />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="工作年限">
              <el-select v-model="form.workYears" placeholder="请选择工作年限" style="width: 100%" clearable>
                <el-option label="应届毕业生" value="应届毕业生" />
                <el-option label="1-3 年" value="1-3 年" />
                <el-option label="3-5 年" value="3-5 年" />
                <el-option label="5-10 年" value="5-10 年" />
                <el-option label="10 年以上" value="10 年以上" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="个人博客">
              <el-input v-model="form.blog" placeholder="https://blog.abc.com/..." clearable />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="代码仓库">
              <el-input v-model="form.github" placeholder="https://github.com/..." clearable />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 二维码链接输入框 -->
        <el-row :gutter="16">
          <el-col :span="24">
            <el-form-item label="二维码链接">
              <el-input v-model="form.qrcodeUrl" placeholder="请输入二维码 URL 或 PNG 格式的 Base64 编码字符串 (data:image/png;base64,...)" clearable />
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider />

        <!-- 2. 教育背景 -->
        <div class="section-header">
          <div class="section-title" style="margin: 0;"><i class="el-icon-reading" /> 教育背景</div>
          <el-button type="success" icon="el-icon-plus" size="mini" plain @click="addEducation">添加教育记录</el-button>
        </div>
        <div v-for="(item, index) in form.educationList" :key="'edu-' + index" class="repeater-item">
          <el-row :gutter="10" style="margin-bottom: 8px;">
            <el-col :span="8">
              <el-input v-model="item.school" placeholder="学校名称" size="small" />
            </el-col>
            <el-col :span="6">
              <el-input v-model="item.major" placeholder="专业名称" size="small" />
            </el-col>
            <el-col :span="6">
              <el-select v-model="item.degree" placeholder="学历/学位" size="small" style="width: 100%" clearable>
                <el-option label="大专" value="大专" />
                <el-option label="本科" value="本科" />
                <el-option label="硕士" value="硕士" />
                <el-option label="博士" value="博士" />
              </el-select>
            </el-col>
            <el-col :span="4" align="right">
              <el-button type="text" icon="el-icon-delete" class="text-danger" @click="removeEducation(index)">删除</el-button>
            </el-col>
          </el-row>
          <el-row :gutter="10">
            <el-col :span="24">
              <el-date-picker
                v-model="item.timeRange"
                type="monthrange"
                range-separator="至"
                start-placeholder="入学时间"
                end-placeholder="毕业时间"
                size="small"
                style="width: 100%"
                value-format="yyyy.MM"
              />
            </el-col>
          </el-row>
        </div>
        <el-empty v-if="form.educationList.length === 0" description="暂未添加教育背景" :image-size="60" />

        <el-divider />

        <!-- 3. 技能特长 -->
        <div class="section-title"><i class="el-icon-star-off" /> 技能特长</div>
        <el-form-item label="专业技能">
          <el-input
            v-model="form.skills"
            type="textarea"
            :rows="3"
            placeholder="例如：熟练掌握 Java/C#，精通 Kubernetes/Docker 容器化部署..."
          />
        </el-form-item>

        <el-divider />

        <!-- 4. 工作经历 -->
        <div class="section-header">
          <div class="section-title" style="margin: 0;"><i class="el-icon-s-cooperation" /> 工作经历</div>
          <el-button type="success" icon="el-icon-plus" size="mini" plain @click="addWorkExperience">添加经历</el-button>
        </div>
        <div v-for="(item, index) in form.workList" :key="'work-' + index" class="repeater-item">
          <el-row :gutter="10" style="margin-bottom: 8px;">
            <el-col :span="10">
              <el-input v-model="item.company" placeholder="公司名称" size="small" />
            </el-col>
            <el-col :span="8">
              <el-input v-model="item.position" placeholder="职位名称" size="small" />
            </el-col>
            <el-col :span="6" align="right">
              <el-button type="text" icon="el-icon-delete" class="text-danger" @click="removeWorkExperience(index)">删除</el-button>
            </el-col>
          </el-row>
          <el-row :gutter="10" style="margin-bottom: 8px;">
            <el-col :span="24">
              <el-date-picker
                v-model="item.timeRange"
                type="monthrange"
                range-separator="至"
                start-placeholder="入职时间"
                end-placeholder="离职时间（留空代表至今）"
                size="small"
                style="width: 100%"
                value-format="yyyy.MM"
              />
            </el-col>
          </el-row>
          <el-row :gutter="10">
            <el-col :span="24">
              <el-input
                v-model="item.content"
                type="textarea"
                :rows="3"
                placeholder="工作内容与业绩贡献（支持使用数字或指标说明成果）"
                size="small"
              />
            </el-col>
          </el-row>
        </div>
        <el-empty v-if="form.workList.length === 0" description="暂无工作经历" :image-size="60" />

        <el-divider />

        <!-- 5. 项目经验 (恢复原有 Row 布局，并在项目名称与项目角色间添加隶属公司) -->
        <div class="section-header">
          <div class="section-title" style="margin: 0;"><i class="el-icon-s-opportunity" /> 项目经验</div>
          <el-button type="success" icon="el-icon-plus" size="mini" plain @click="addProject">添加项目</el-button>
        </div>
        <div v-for="(item, index) in form.projectList" :key="'proj-' + index" class="repeater-item">
          <el-row :gutter="10" style="margin-bottom: 8px;">
            <el-col :span="7">
              <el-input v-model="item.projectName" placeholder="项目名称" size="small" />
            </el-col>
            <el-col :span="7">
              <el-input v-model="item.company" placeholder="隶属公司" size="small" />
            </el-col>
            <el-col :span="6">
              <el-input v-model="item.role" placeholder="项目角色（如：核心开发）" size="small" />
            </el-col>
            <el-col :span="4" align="right">
              <el-button type="text" icon="el-icon-delete" class="text-danger" @click="removeProject(index)">删除</el-button>
            </el-col>
          </el-row>
          <el-row :gutter="10" style="margin-bottom: 8px;">
            <el-col :span="24">
              <el-date-picker
                v-model="item.timeRange"
                type="monthrange"
                range-separator="至"
                start-placeholder="开始时间"
                end-placeholder="结束时间"
                size="small"
                style="width: 100%"
                value-format="yyyy.MM"
              />
            </el-col>
          </el-row>
          <el-row :gutter="10">
            <el-col :span="24">
              <el-input
                v-model="item.description"
                type="textarea"
                :rows="3"
                placeholder="项目背景、技术栈应用及关键难点攻克..."
                size="small"
              />
            </el-col>
          </el-row>
        </div>
        <el-empty v-if="form.projectList.length === 0" description="暂无项目经验" :image-size="60" />

        <el-divider />

        <!-- 6. 个人项目 -->
        <div class="section-header">
          <div class="section-title" style="margin: 0;"><i class="el-icon-share" /> 个人项目</div>
          <el-button type="success" icon="el-icon-plus" size="mini" plain @click="addPersonalProject">添加个人项目</el-button>
        </div>
        <div v-for="(item, index) in form.personalProjectList" :key="'pp-' + index" class="repeater-item">
          <div class="repeater-header">
            <span class="repeater-index">个人项目 #{{ index + 1 }}</span>
            <el-button type="text" icon="el-icon-delete" class="text-danger" @click="removePersonalProject(index)">删除</el-button>
          </div>
          <div class="repeater-fields">
            <div class="form-row">
              <span class="field-label">项目名称：</span>
              <el-input v-model="item.projectName" placeholder="例如: qshot / qchat" size="small" clearable />
            </div>
            <div class="form-row">
              <span class="field-label">项目地址：</span>
              <el-input v-model="item.codeRepo" placeholder="代码仓库链接或标签" size="small" clearable />
            </div>
            <div class="form-row textarea-row">
              <span class="field-label">项目简介：</span>
              <el-input
                v-model="item.description"
                type="textarea"
                :rows="3"
                placeholder="个人项目简介、技术栈特点及核心功能说明..."
                size="small"
              />
            </div>
          </div>
        </div>
        <el-empty v-if="form.personalProjectList.length === 0" description="暂无个人项目" :image-size="60" />

        <el-divider />

        <!-- 7. 自我评价 -->
        <div class="section-title"><i class="el-icon-user" /> 自我评价</div>
        <el-form-item label="自我评价">
          <el-input
            v-model="form.selfEvaluation"
            type="textarea"
            :rows="3"
            placeholder="具备较强的团队协作与技术攻坚能力..."
          />
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 弹窗：预览区 -->
    <el-dialog
      title="📄 简历预览 (A4 风格)"
      :visible.sync="showPreviewDialog"
      width="900px"
      append-to-body
      center
      custom-class="resume-preview-dialog"
    >
      <div id="printResumeArea" class="preview-card-wrapper box-card">
        <!-- 弹窗顶部：左侧上下结构，右侧二维码与提示 -->
        <div class="preview-top-banner">
          <div class="preview-banner-left">
            <h2 class="preview-person-name">{{ form.name || '姓名未填写' }}</h2>
            <div v-if="form.targetPosition" class="preview-target-job">
              <i class="el-icon-position" /> {{ form.targetPosition }}
            </div>
          </div>
          <div v-if="form.qrcodeUrl" class="preview-banner-right">
            <img :src="form.qrcodeUrl" alt="微信二维码" class="preview-qrcode">
            <span class="qrcode-tip">扫我加微信</span>
          </div>
        </div>

        <el-divider />

        <!-- 基本信息展示 -->
        <div class="section-title"><i class="el-icon-s-custom" /> 基本信息</div>
        <div class="preview-info-grid">
          <div class="preview-info-item"><strong>姓名：</strong><span>{{ form.name || '-' }}</span></div>
          <div class="preview-info-item"><strong>求职意向：</strong><span>{{ form.targetPosition || '-' }}</span></div>
          <div class="preview-info-item"><strong>手机号：</strong><span>{{ form.phone || '-' }}</span></div>
          <div class="preview-info-item"><strong>电子邮箱：</strong><span>{{ form.email || '-' }}</span></div>
          <div class="preview-info-item"><strong>年龄：</strong><span>{{ form.age || '-' }}</span></div>
          <div class="preview-info-item"><strong>工作年限：</strong><span>{{ form.workYears || '-' }}</span></div>
          <div class="preview-info-item"><strong>个人博客：</strong><span>{{ form.blog || '-' }}</span></div>
          <div class="preview-info-item"><strong>代码仓库：</strong><span>{{ form.github || '-' }}</span></div>
        </div>

        <el-divider />

        <!-- 教育背景展示 -->
        <div class="section-title"><i class="el-icon-reading" /> 教育背景</div>
        <div v-for="(edu, eIdx) in form.educationList" :key="'p-edu-' + eIdx" class="preview-sub-item">
          <div class="preview-item-row">
            <strong>{{ edu.school || '学校名称' }}</strong>
            <span>{{ edu.major || '专业' }} | {{ edu.degree || '学历' }}</span>
            <span class="text-muted">{{ formatTimeRange(edu.timeRange) }}</span>
          </div>
        </div>
        <el-empty v-if="!form.educationList || form.educationList.length === 0" description="暂无教育背景" :image-size="40" />

        <el-divider />

        <!-- 技能特长展示 -->
        <div class="section-title"><i class="el-icon-star-off" /> 技能特长</div>
        <p class="preview-text pre-wrap">{{ form.skills || '暂无填写' }}</p>

        <el-divider />

        <!-- 工作经历展示 -->
        <div class="section-title"><i class="el-icon-s-cooperation" /> 工作经历</div>
        <div v-for="(work, wIdx) in form.workList" :key="'p-work-' + wIdx" class="preview-sub-item">
          <div class="preview-item-row">
            <strong>{{ work.company || '公司名称' }}</strong>
            <span class="text-primary">{{ work.position || '职位' }}</span>
            <span class="text-muted">{{ formatTimeRange(work.timeRange) }}</span>
          </div>
          <p class="preview-text pre-wrap">{{ work.content || '暂无内容' }}</p>
        </div>
        <el-empty v-if="!form.workList || form.workList.length === 0" description="暂无工作经历" :image-size="40" />

        <el-divider />

        <!-- 项目经验展示 (原有的多端对齐布局) -->
        <div class="section-title"><i class="el-icon-s-opportunity" /> 项目经验</div>
        <div v-for="(proj, pIdx) in form.projectList" :key="'p-proj-' + pIdx" class="preview-sub-item">
          <div class="preview-item-row">
            <div>
              <strong>{{ proj.projectName || '项目名称' }}</strong>
              <span v-if="proj.company" class="company-tag">（{{ proj.company }}）</span>
            </div>
            <span class="text-primary">{{ proj.role || '角色' }}</span>
            <span class="text-muted">{{ formatTimeRange(proj.timeRange) }}</span>
          </div>
          <p class="preview-text pre-wrap">{{ proj.description || '暂无描述' }}</p>
        </div>
        <el-empty v-if="!form.projectList || form.projectList.length === 0" description="暂无项目经验" :image-size="40" />

        <el-divider />

        <!-- 个人项目展示 -->
        <div class="section-title"><i class="el-icon-share" /> 个人项目</div>
        <div v-for="(pp, ppIdx) in form.personalProjectList" :key="'p-pp-' + ppIdx" class="preview-project-card">
          <div class="preview-field-row">
            <span class="preview-field-label">项目名称：</span>
            <span class="preview-field-value"><strong>{{ pp.projectName || '未命名项目' }}</strong></span>
          </div>
          <div v-if="pp.codeRepo" class="preview-field-row">
            <span class="preview-field-label">项目地址：</span>
            <span class="preview-field-value">{{ pp.codeRepo }}</span>
          </div>
          <div v-if="pp.description" class="preview-field-row">
            <span class="preview-field-label">项目简介：</span>
            <span class="preview-field-value preview-text pre-wrap">{{ pp.description }}</span>
          </div>
        </div>
        <el-empty v-if="!form.personalProjectList || form.personalProjectList.length === 0" description="暂无个人项目" :image-size="40" />

        <el-divider />

        <!-- 自我评价展示 -->
        <div class="section-title"><i class="el-icon-user" /> 自我评价</div>
        <p class="preview-text pre-wrap">{{ form.selfEvaluation || '暂无填写' }}</p>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="showPreviewDialog = false">关 闭</el-button>
        <el-button type="primary" size="small" icon="el-icon-printer" @click="handlePrint">打印 / 导出 A4 PDF</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { addResume, getResume } from '@/api/account'

export default {
  name: 'ResumeView',
  data() {
    return {
      submitLoading: false,
      showPreviewDialog: false,
      form: {
        name: '',
        targetPosition: '',
        phone: '',
        email: '',
        age: '',
        workYears: '',
        blog: '',
        github: '',
        qrcodeUrl: '',
        skills: '',
        selfEvaluation: '',
        educationList: [],
        workList: [],
        projectList: [],
        personalProjectList: []
      },
      rules: {
        name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
        targetPosition: [{ required: true, message: '请输入目标岗位', trigger: 'blur' }],
        email: [
          { required: true, message: '请输入电子邮箱', trigger: 'blur' },
          { type: 'email', message: '邮箱格式不正确', trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    document.title = 'resume'
    this.getData()
  },
  methods: {
    getData() {
      getResume().then(resp => {
        if (resp.code === 0 && resp.data) {
          this.form = typeof resp.data === 'string' ? JSON.parse(resp.data) : resp.data
        } else if (resp.code !== 0 && resp.code !== undefined) {
          this.$message.error(resp.msg || '获取简历数据失败')
        }
      }).catch(error => {
        this.$message.error(error.message || '网络异常')
      })
    },
    addEducation() {
      this.form.educationList.push({ school: '', major: '', degree: '', timeRange: [] })
    },
    removeEducation(index) {
      this.form.educationList.splice(index, 1)
    },
    addWorkExperience() {
      this.form.workList.push({ company: '', position: '', timeRange: [], content: '' })
    },
    removeWorkExperience(index) {
      this.form.workList.splice(index, 1)
    },
    addProject() {
      this.form.projectList.push({ projectName: '', company: '', role: '', timeRange: [], description: '' })
    },
    removeProject(index) {
      this.form.projectList.splice(index, 1)
    },
    addPersonalProject() {
      this.form.personalProjectList.push({ projectName: '', codeRepo: '', description: '' })
    },
    removePersonalProject(index) {
      this.form.personalProjectList.splice(index, 1)
    },
    formatTimeRange(range) {
      if (!range || range.length === 0) return ''
      return `${range[0]} 至 ${range[1] || '至今'}`
    },
    handlePreview() {
      this.$refs.formRef.validate(valid => {
        if (valid) {
          this.showPreviewDialog = true
        } else {
          this.$message.warning('请先补全基础必填选项')
        }
      })
    },
    handleSubmit() {
      this.$refs.formRef.validate(valid => {
        if (!valid) return
        this.submitLoading = true
        addResume(this.form).then(resp => {
          if (resp.code === 0) {
            this.$message.success('保存成功')
          } else {
            this.$message.error(resp.msg || '保存失败')
          }
        }).catch(error => {
          this.$message.error(error.message || '网络异常')
        }).finally(() => {
          this.submitLoading = false
        })
      })
    },
    handlePrint() {
      window.print()
    }
  }
}
</script>

<style scoped>
.resume-create-container {
  padding: 24px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.box-card {
  border-radius: 8px;
  border: 1px solid #e6ebf5;
  max-width: 1000px;
  margin: 0 auto;
  background-color: #fff;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-wrapper {
  display: flex;
  align-items: center;
}

.header-icon {
  font-size: 20px;
  color: #409eff;
  margin-right: 8px;
}

.title-text {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.sub-title {
  font-size: 12px;
  color: #909399;
  margin-left: 12px;
}

.header-action {
  display: flex;
  gap: 10px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin: 16px 0 12px 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 基础通用 repeater-item */
.repeater-item {
  background-color: #fafbfc;
  border: 1px dashed #dcdfe6;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 12px;
}

/* 个人项目专用编辑卡片中的键值行样式 */
.repeater-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #dcdfe6;
}

.repeater-index {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
}

.form-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.form-row.textarea-row {
  align-items: flex-start;
}

.field-label {
  width: 75px;
  flex-shrink: 0;
  font-size: 13px;
  color: #606266;
  font-weight: 500;
  text-align: right;
  padding-right: 12px;
}

.form-row ::v-deep .el-input,
.form-row ::v-deep .el-textarea {
  flex: 1;
}

/* 预览模式下的个人项目卡片样式 */
.preview-project-card {
  background-color: #fafbfc;
  border: 1px solid #e4e7ed;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 12px;
}

.preview-field-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 6px;
  font-size: 13px;
  color: #303133;
  line-height: 1.5;
}

.preview-field-row:last-child {
  margin-bottom: 0;
}

.preview-field-label {
  width: 75px;
  flex-shrink: 0;
  color: #909399;
  font-weight: 500;
  text-align: right;
  padding-right: 8px;
}

.preview-field-value {
  flex: 1;
  word-break: break-all;
}

.company-tag {
  font-size: 12px;
  color: #606266;
  font-weight: normal;
  margin-left: 2px;
}

.text-danger {
  color: #f56c6c;
}

.text-primary {
  color: #409eff;
}

.text-muted {
  color: #909399;
  font-size: 12px;
}

/* 预览弹窗内部的卡片样式复用 */
.preview-card-wrapper {
  padding: 24px;
  max-height: 70vh;
  overflow-y: auto;
  box-shadow: none !important;
  border: none !important;
}

/* 顶部横幅：左侧上下结构，右侧二维码与提示 */
.preview-top-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
}

.preview-banner-left {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-person-name {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
}

.preview-target-job {
  font-size: 14px;
  font-weight: 600;
  color: #409eff;
  background-color: #ecf5ff;
  border: 1px solid #d9ecff;
  padding: 4px 12px;
  border-radius: 12px;
  display: inline-block;
  width: fit-content;
}

.preview-banner-right {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.preview-qrcode {
  width: 105px;
  height: 105px;
  object-fit: cover;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  padding: 4px;
  background-color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.qrcode-tip {
  font-size: 11px;
  color: #909399;
  letter-spacing: 0.5px;
  font-weight: 500;
  text-align: center;
}

.preview-info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 20px;
  font-size: 13px;
  color: #606266;
}

.preview-sub-item {
  margin-bottom: 12px;
}

.preview-item-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #303133;
  margin-bottom: 4px;
}

.preview-text {
  font-size: 12.5px;
  color: #606266;
  line-height: 1.6;
  margin: 0;
}

.pre-wrap {
  white-space: pre-wrap;
}

.dialog-footer {
  text-align: right;
}

/* ==================== A4 纸打印与 PDF 导出优化样式 ==================== */
@page {
  size: A4;
  margin: 10mm;
}

@media print {
  body * {
    visibility: hidden;
  }

  #printResumeArea,
  #printResumeArea * {
    visibility: visible;
  }

  #printResumeArea {
    position: absolute;
    left: 0;
    top: 0;
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    box-shadow: none !important;
    border: none !important;
    max-height: none !important;
    overflow: visible !important;
    box-sizing: border-box !important;
  }

  .el-dialog__header,
  .el-dialog__footer,
  .dialog-footer {
    display: none !important;
  }

  .el-dialog,
  .el-dialog__wrapper {
    background: transparent !important;
    box-shadow: none !important;
    position: static !important;
  }
}
</style>
